import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Box, IconButton, Snackbar, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  publishNotification,
  publishNotice,
  dismissNotice,
  enqueueNotification,
  subscribeNotifications,
  type NotificationEvent,
  type NotificationTone,
} from "@/shared/notifications/notification-bus";

interface NotificationContextValue {
  showNotification: (message: string, tone?: NotificationTone, source?: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const notification = notifications[0] ?? null;

  useEffect(
    () => subscribeNotifications((next) => setNotifications((current) => enqueueNotification(current, next))),
    [],
  );
  const advance = () => {
    notification?.onDismiss?.();
    setNotifications((current) => current.slice(1));
  };

  const value = useMemo<NotificationContextValue>(
    () => ({
      showNotification: (message, tone = "default", source) => {
        if (source) publishNotice({ id: source, message, tone });
        else publishNotification(message, tone);
      },
    }),
    [],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        key={notification ? `${notification.tone}:${notification.message}` : "empty"}
        open={notification !== null}
        autoHideDuration={notification?.tone === "error" || notification?.action ? null : 7000}
        onClose={(_event, reason) => {
          if (reason !== "clickaway") advance();
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ bottom: 24 }}
      >
        <Box
          role={notification?.tone === "error" ? "alert" : "status"}
          sx={{
            minWidth: { xs: 0, sm: 300 },
            maxWidth: "min(560px, calc(100vw - 32px))",
            maxHeight: "60dvh",
            overflowY: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.25,
            bgcolor: "background.paper",
            color: notification?.tone === "error" ? "secondary.main" : "primary.main",
            border: "2px solid",
            borderColor: notification?.tone === "error" ? "secondary.main" : "primary.main",
            boxShadow: "8px 8px 0 #0a0a0a",
          }}
        >
          <Typography component="div" sx={{ flex: 1, fontSize: "0.875rem", overflowWrap: "anywhere", fontWeight: 700 }}>
            {notification?.content ?? notification?.message}
          </Typography>
          {notification?.action}
          <IconButton aria-label="Dismiss notification" size="small" onClick={advance} sx={{ color: "inherit" }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  const id = useId();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      dismissNotice(id);
    };
  }, [id]);
  const value = useMemo<NotificationContextValue>(
    () => ({
      showNotification: (message, tone) => {
        if (active.current) context?.showNotification(message, tone, id);
      },
    }),
    [context, id],
  );
  if (!context) throw new Error("useNotification must be used within NotificationProvider");
  return value;
}
